<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" aria-labelledby="examConsentOffcanvasLabel" ref="offcanvasElement">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" @click="doCancel">
        <IconArrowBack />
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block">{{ $t('clinicClaim.previewTitle') }}</h5>
    </div>

    <div class="offcanvas-body">
      <p class="preview-subtitle">{{ $t('clinicClaim.previewSubtitle') }}</p>

      <div class="alert" :class="`alert-${alertType}`" role="alert" v-if="alertType">
        <h4>{{ $t(alertTitle) }}</h4>

        <ul>
          <li v-for="(item, i) in alertItems" :key="i">
            <p>{{ $te(item) ? $t(item) : item }}</p>
          </li>
        </ul>
      </div>

      <template v-if="previewData">
        <div class="preview-meta">
          <span v-if="previewData.patientName"><strong>Patient:</strong> {{ previewData.patientName }}</span>
          <span><strong>File:</strong> {{ previewData.fileName }}</span>
          <span><strong>Bytes:</strong> {{ previewData.bytes }}</span>
          <span v-if="previewData.claimCount"><strong>Claims:</strong> {{ previewData.claimCount }}</span>
        </div>

        <pre class="preview-x12">{{ formattedPreview }}</pre>
      </template>
    </div>

    <div class="offcanvas-footer">
      <button class="btn btn-success" type="button" @click="downloadPreview" v-if="previewData">{{ $t('clinicClaim.previewDownload') }}</button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  width:                         90% !important;
  gap:                           26px;

  .offcanvas-title {
    margin-bottom:               0;
  }

  .offcanvas-body {
    .preview-subtitle {
      font-size:                 12px;
      color:                     var(--Complementary-Grey, #757575);
      font-style:                italic;
      margin-bottom:             10px;
    }

    .preview-meta {
      @include                   border_radius(4px);
      color:                     var(--Complementary-Grey-Dark, #424242);
      font-size:                 12px;
      display:                   flex;
      flex-wrap:                 wrap;
      gap:                       16px;
      padding:                   8px 12px;
      margin-bottom:             12px;
      background:                var(--Complementary-Grey-BG, #f5f5f5);
    }

    .alert {
      @include                   border_radius(10px);
      padding:                   10px;

      .btn-close {
        position:                absolute;
        top:                     10px;
        right:                   10px;
        font-size:               var(--font-size-12);
      }

      ul {
        gap:                     5px;
        margin:                  0;
      }

      h4 {
        font-size:               var(--font-size-14);
        font-weight:             400;
        line-height:             140%; /* 19.6px */
        margin-bottom:           10px;
      }

      p {
        font-size:               var(--font-size-12);
        margin:                  0;
        padding:                 0;
      }

      &.alert-warning {
        background:              #FFEBCB;
        border:                  1px solid #D1A660;

        h4,
        p {
          color:                 var(--Brand-Primary-BG);
        }
      }

      &.alert-danger {
        p {
          color:                 inherit;
        }
      }
    }

    .preview-x12 {
      @include                   border_radius(6px);
      color:                     #d4d4d4;
      font-family:               'Monaco', 'Menlo', 'Courier New', monospace;
      font-size:                 11px;
      line-height:               1.5;
      white-space:               pre-wrap;
      word-break:                break-all;
      padding:                   12px 16px;
      margin:                    0;
      background:                #1e1e1e;
      overflow-x:                auto;
    }
  }

  .offcanvas-footer {
    display:                     flex;
    flex-direction:              row;
    gap:                         26px;
  }

  .btn {
    font-size:                   var(--font-size-14);
    font-weight:                 700;
    line-height:                 normal;
    letter-spacing:              4.2px;
    text-align:                  center;
    text-transform:              uppercase;
    width:                       100%;
    max-width:                   350px;
    margin:                      0;
    padding:                     20px;
  }
}

@media (max-width: 991px) {
  .offcanvas {
    .offcanvas-body {
      .alert {
        h4 {
          padding-right:         20px;
        }
      }
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user';

export default {
  name: 'EdiClaimPreviewOffcanvas',
  props: {
    offcanvas_id: { type: [String, Number] },
  },
  data: () => ({
    loading: false,
    session: null,
    previewErrors: [],
    previewData: null,
    clinicID: null,
    examID: null,
    overrides: null,
    offcanvas_claim_preview: null,
  }),
  computed: {
    alertType() {
      if (this.previewErrors.length)
        return 'danger'

      if (this.previewData?.warnings?.length)
        return 'warning'

      return null
    },
    alertTitle() {
      if (this.previewErrors.length)
        return 'clinicClaim.previewValidationFailed'

      return 'examClaim.invalidWarning'
    },
    alertItems() {
      if (this.previewErrors.length)
        return this.previewErrors

      return this.previewData?.warnings || []
    },
    formattedPreview() {
      if (!this.previewData?.content) return ''
      return this.previewData.content.replace(/~/g, '~\n')
    }
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.clinicID = this.session.currentClinic && this.session.currentClinic.id
  },
  methods: {
    show(options = {}) {
      this.previewData = null
      this.previewErrors = []
      this.examID = options.examID || null
      this.overrides = options.overrides || null

      this.fetchData()

      const el = document.getElementById(this.offcanvas_id)
      if (el) {
        this.offcanvas_claim_preview = bootstrap.Offcanvas.getOrCreateInstance(el)
        this.offcanvas_claim_preview.show()
      }

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doCancel() {
      if (this.offcanvas_claim_preview) this.offcanvas_claim_preview.hide()
      this.offcanvas_claim_preview = null
      this.resolvePromise?.(false)
    },

    async fetchData() {
      this.loading = true
      NProgress.start()
      try {
        let data

        if (this.examID) {
          const result = await this.$claimConfigService.previewExam({examId: this.examID})
          data = {
            fileName: result.fileName,
            bytes: result.bytes,
            content: result.content,
            patientName: result.patientName || null,
            warnings: result.valid ? [] : (result.errors || [])
          }
        } else {
          data = await this.$claimConfigService.preview({
            clinicId: this.clinicID,
            claimCount: 1,
            overrides: this.overrides
          })
        }

        this.previewData = data
      } catch (err) {
        const errors = err.response?.data?.errors
        if (errors && errors.length) {
          this.previewErrors = errors
        } else {
          this.$message.popup(this, this.$i18n.t('clinicClaim.previewError'), 'operationFail', 'error')
        }
      } finally {
        NProgress.done(true)
        this.loading = false
      }
    },

    downloadPreview() {
      if (!this.previewData)
        return

      const blob = new Blob([this.previewData.content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = this.previewData.fileName || 'claim_preview.txt'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  }
};
</script>
